import React, { useEffect, useState } from "react";
import { getAllVip, deleteVip } from "../api/vipform";
import { useVipStore } from "../context/VipContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export default function VipDetails() {
  const { handleEdit, refreshTrigger } = useVipStore();
  const [vipList, setVipList] = useState([]);

  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const role = localStorage.getItem("role"); // "admin" | "user"

  /* FETCH VIP LIST */
  useEffect(() => {
    async function fetchVips() {
      try {
        const data = await getAllVip();

        if (Array.isArray(data)) setVipList(data);
        else if (Array.isArray(data?.data)) setVipList(data.data);
        else if (Array.isArray(data?.vip)) setVipList(data.vip);
        else if (Array.isArray(data?.designation)) setVipList(data.designation);
      } catch (err) {
        toast.error("Failed to load VIPs!");
      }
    }
    fetchVips();
  }, [refreshTrigger]);

  /* DELETE */
  const handleDelete = async (vip) => {
    try {
      await deleteVip(vip.id);
      const updated = await getAllVip();
      setVipList(Array.isArray(updated) ? updated : []);
      window.alert("VIP Deleted Successfully!");
    } catch (err) {
      toast.error("Failed to delete VIP!");
    }
  };

  /* UNIQUE DESIGNATIONS */
  const designations = [
    ...new Set(vipList.map((g) => g.designation || "Uncategorized")),
  ];

  /* FILTER + SEARCH */
  const filteredVips = vipList.filter((v) => {
    const validStatus = v.status === "Active" || v.status === "Inactive";
    if (!validStatus) return false;

    const desMatch = selectedDesignation ? v.designation === selectedDesignation : true;
    const statusMatch = selectedStatus ? v.status === selectedStatus : true;

    const text = searchText.trim().toLowerCase();
    const searchMatch = text
      ? [v.id, v.name, v.email, v.contactno, v.designation, v.status].some(
          (field) => String(field || "").toLowerCase().includes(text)
        )
      : true;

    return desMatch && statusMatch && searchMatch;
  });

  /* PAGINATION */
  const totalPages = Math.ceil(filteredVips.length / rowsPerPage);
  const currentData = filteredVips.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const pageWindow = 2;
  const startPage = Math.max(1, currentPage - Math.floor(pageWindow / 2));
  const endPage = Math.min(totalPages, startPage + pageWindow - 1);
  const visiblePages = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  return (
    <>
      <div className="vipdetails-wrapper">
        {/* Header */}
        <header className="page-header">
          <div className="header-content">
            <h1 className="page-title">VIP Records</h1>
            <div className="total-card">
              <span className="total-count">{vipList.length}</span>
              <span className="total-label">Total VIPs</span>
            </div>
          </div>
        </header>

        {/* Filters */}
        <section className="filters-section">
          <div className="filter-group">
            <label>Designation</label>
            <select
              value={selectedDesignation}
              onChange={(e) => {
                setSelectedDesignation(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Designations</option>
              {designations.map((d, i) => (
                <option key={i} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="filter-group search-group">
            <label>Search</label>
            <div className="search-input-wrapper">
              <i className="fas fa-search"></i>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Name, Email, Contact, ID..."
              />
            </div>
          </div>
        </section>

        {/* Table / Cards */}
        <section className="table-section">
          {currentData.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-users-slash"></i>
              <h3>No VIP Records Found</h3>
              <p>Try adjusting your filters or add a new VIP.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container desktop-only">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>VIP ID</th>
                      <th>Email</th>
                      <th>Designation</th>
                      <th>Contact</th>
                      <th>Status</th>
                      {role !== "user" && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((vip, index) => (
                      <tr
                        key={vip.id || index}
                        className="table-row"
                        onClick={() => {
                          localStorage.setItem("selectedVip", JSON.stringify(vip));
                          if (vip.status === "Inactive") {
                            navigate("/dutyviphistory");
                          } else {
                            navigate("/vipassignedofficers");
                          }
                        }}
                      >
                        <td>{index + 1 + (currentPage - 1) * rowsPerPage}</td>
                        <td className="name-cell">{vip.name}</td>
                        <td>{vip.id}</td>
                        <td>{vip.email}</td>
                        <td>
                          <span className="badge">{vip.designation || "—"}</span>
                        </td>
                        <td>{vip.contactno}</td>
                        <td>
                          <span className={`status-badge ${vip.status.toLowerCase()}`}>
                            {vip.status}
                          </span>
                        </td>
                        {role !== "user" && (
                          <td className="actions-cell">
                            <button
                              className="action-btn edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(vip);
                                navigate("/viplist");
                              }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="action-btn delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(vip);
                              }}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards mobile-only">
                {currentData.map((vip, index) => (
                  <div
                    key={vip.id || index}
                    className="vip-card"
                    onClick={() => {
                      localStorage.setItem("selectedVip", JSON.stringify(vip));
                      if (vip.status === "Inactive") {
                        navigate("/dutyviphistory");
                      } else {
                        navigate("/vipassignedofficers");
                      }
                    }}
                  >
                    <div className="card-header">
                      <span className="index">#{index + 1 + (currentPage - 1) * rowsPerPage}</span>
                      <span className={`status-badge ${vip.status.toLowerCase()}`}>
                        {vip.status}
                      </span>
                    </div>
                    <h3 className="vip-name">{vip.name}</h3>
                    <div className="card-details">
                      <div><strong>ID:</strong> {vip.id}</div>
                      <div><strong>Email:</strong> {vip.email}</div>
                      <div><strong>Designation:</strong> <span className="badge">{vip.designation || "—"}</span></div>
                      <div><strong>Contact:</strong> {vip.contactno}</div>
                    </div>
                    {role !== "user" && (
                      <div className="card-actions">
                        <button
                          className="action-btn edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(vip);
                            navigate("/viplist");
                          }}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(vip);
                          }}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              {visiblePages.map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`page-btn ${currentPage === page ? "active" : ""}`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                <i className="fas fa-chevron-right"></i>
              </button>

              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rows-select"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={30}>30 / page</option>
              </select>
            </div>
          )}
        </section>
      </div>

      <style jsx>{`
        .vipdetails-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 20px;
        }

        .page-header {
          margin-bottom: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #60a5fa;
        }

        .total-card {
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          padding: 16px 24px;
          border-radius: 16px;
          text-align: center;
        }

        .total-count {
          display: block;
          font-size: 1.8rem;
          font-weight: 800;
          color: #60a5fa;
        }

        .total-label {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        .filters-section {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
        }

        .filter-group {
          flex: 1;
          min-width: 220px;
          display: flex;
          flex-direction: column;
        }

        .filter-group label {
          font-weight: 600;
          margin-bottom: 8px;
          color: white;
        }

        .filter-group select,
        .search-input-wrapper input {
          padding: 14px 18px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 1rem;
        }

        .filter-group select:focus,
        .search-input-wrapper input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
        }

        .search-input-wrapper {
          position: relative;
        }

        .search-input-wrapper i {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-input-wrapper input {
          padding-left: 48px;
        }

        .table-section {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
          overflow: hidden;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #94a3b8;
        }

        .empty-state i {
          font-size: 4rem;
          opacity: 0.4;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          margin-bottom: 10px;
          color: white;
        }

        /* Desktop Table */
        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: rgba(59, 130, 246, 0.15);
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #60a5fa;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table-row {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .table-row:hover {
          background: rgba(59, 130, 246, 0.1);
          transform: translateY(-2px);
        }

        .table-row td {
          padding: 18px 16px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .name-cell {
          font-weight: 600;
          color: white;
        }

        .badge {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.active {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .status-badge.inactive {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .actions-cell {
          display: flex;
          gap: 12px;
        }

        .action-btn {
          padding: 10px 14px;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .action-btn.edit {
          background: #10b981;
          color: white;
        }

        .action-btn.edit:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .action-btn.delete {
          background: #ef4444;
          color: white;
        }

        .action-btn.delete:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        /* Mobile Cards */
        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .vip-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .vip-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.15);
          background: rgba(15, 23, 42, 0.8);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .index {
          font-weight: 600;
          color: #60a5fa;
        }

        .vip-name {
          font-size: 1.3rem;
          font-weight: 700;
          color: white;
          margin-bottom: 12px;
        }

        .card-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
          font-size: 0.95rem;
        }

        .card-details div strong {
          color: #60a5fa;
        }

        .card-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        /* Pagination */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-top: 30px;
          flex-wrap: wrap;
        }

        .page-btn {
          width: 44px;
          height: 44px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-btn:hover:not(:disabled) {
          background: #3b82f6;
          transform: translateY(-2px);
        }

        .page-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          font-weight: 600;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .rows-select {
          padding: 12px 16px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .desktop-only { display: none; }
          .mobile-only { display: flex; }
          .card-details { grid-template-columns: 1fr; }
        }

        @media (min-width: 992px) {
          .mobile-only { display: none; }
          .desktop-only { display: block; }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .filters-section {
            flex-direction: column;
          }

          .filter-group {
            min-width: auto;
          }

          .page-title {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </>
  );
}


// import React, { useEffect, useState } from "react";
// import { getAllVip, deleteVip } from "../api/vipform";
// import { useVipStore } from "../context/VipContext";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";

// export default function VipDetails() {
//   const { handleEdit, refreshTrigger } = useVipStore();
//   const [vipList, setVipList] = useState([]);

//   const [selectedDesignation, setSelectedDesignation] = useState("");
//   const [selectedStatus, setSelectedStatus] = useState("");
//   const [searchText, setSearchText] = useState("");
//   const navigate = useNavigate();

//   const [currentPage, setCurrentPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(30);
//   const role = localStorage.getItem("role"); // "admin" | "user"

//   /* FETCH VIP LIST */
//   useEffect(() => {
//     async function fetchVips() {
//       try {
//         const data = await getAllVip();

//         if (Array.isArray(data)) setVipList(data);
//         else if (Array.isArray(data?.data)) setVipList(data.data);
//         else if (Array.isArray(data?.vip)) setVipList(data.vip);
//         else if (Array.isArray(data?.designation)) setVipList(data.designation);
//       } catch (err) {
//         toast.error("Failed to load VIPs!");
//       }
//     }
//     fetchVips();
//   }, [refreshTrigger]);

//   /* DELETE */
//   const handleDelete = async (vip) => {
//     console.log(vip.id);
//     try {
//       await deleteVip(vip.id);
//       const updated = await getAllVip();
//       setVipList(Array.isArray(updated) ? updated : []);
//       toast.success("VIP Deleted Successfully!");
//     } catch (err) {
//       toast.error("Failed to delete VIP!");
//     }
//   };

//   /* UNIQUE DESIGNATIONS */
//   const designations = [
//     ...new Set(vipList.map((g) => g.designation || "Uncategorized")),
//   ];

//   /* FILTER + SEARCH */
//   const filteredVips = vipList.filter((v) => {
//     // ✅ ALLOW ONLY Active / Inactive
//     const validStatus = v.status === "Active" || v.status === "Inactive";

//     if (!validStatus) return false;

//     const desMatch = selectedDesignation
//       ? v.designation === selectedDesignation
//       : true;

//     const statusMatch = selectedStatus ? v.status === selectedStatus : true;

//     const text = searchText.trim().toLowerCase();
//     const searchMatch = text
//       ? [v.id, v.name, v.email, v.contactno, v.designation, v.status].some(
//           (field) =>
//             String(field || "")
//               .toLowerCase()
//               .includes(text)
//         )
//       : true;

//     return desMatch && statusMatch && searchMatch;
//   });

//   /* PAGINATION */
//   const totalPages = Math.ceil(filteredVips.length / rowsPerPage);

//   const currentData = filteredVips.slice(
//     (currentPage - 1) * rowsPerPage,
//     currentPage * rowsPerPage
//   );

//   const goToPage = (page) => {
//     if (page >= 1 && page <= totalPages) setCurrentPage(page);
//   };

//   /* SHIFT PAGINATION */
//   const pageWindow = 2;
//   const startPage = Math.max(1, currentPage - Math.floor(pageWindow / 2));
//   const endPage = Math.min(totalPages, startPage + pageWindow - 1);
//   const visiblePages = Array.from(
//     { length: endPage - startPage + 1 },
//     (_, i) => startPage + i
//   );

//   return (
//     <div style={styles.page}>
//       {/* HEADER */}
//       <div style={styles.headerRow}>
//         <h2 style={styles.title}>VIP Records</h2>
//         <div style={styles.totalBox}>
//           <span style={styles.totalNumber}>{vipList.length}</span>
//           <span style={styles.totalLabel}>Total VIPs</span>
//         </div>
//       </div>

//       {/* FILTER ROW */}
//       <div style={styles.filterRow}>
//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Designation</label>
//           <select
//             value={selectedDesignation}
//             onChange={(e) => {
//               setSelectedDesignation(e.target.value);
//               setCurrentPage(1);
//             }}
//             style={styles.select}
//           >
//             <option value="">All</option>
//             {designations.map((d, i) => (
//               <option key={i}>{d}</option>
//             ))}
//           </select>
//         </div>

//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Status</label>
//           <select
//             value={selectedStatus}
//             onChange={(e) => {
//               setSelectedStatus(e.target.value);
//               setCurrentPage(1);
//             }}
//             style={styles.select}
//           >
//             <option value="">All</option>
//             <option>Active</option>
//             <option>Inactive</option>
//           </select>
//         </div>

//         <div style={{ ...styles.filterCard, flex: "2 1 300px" }}>
//           <label style={styles.filterLabel}>Search</label>
//           <input
//             type="text"
//             value={searchText}
//             onChange={(e) => {
//               setSearchText(e.target.value);
//               setCurrentPage(1);
//             }}
//             placeholder="Search by Name, Email, Contact..."
//             style={styles.searchInput}
//           />
//         </div>
//       </div>

//       {/*  RESPONSIVE TABLE WRAPPER */}
//       <div style={styles.tableWrapper}>
//         <div style={styles.tableCard}>
//           {currentData.length === 0 ? (
//             <div style={styles.noData}>No VIP Records Found</div>
//           ) : (
//             <table style={styles.table}>
//               <thead>
//                 <tr>
//                   <th style={styles.th}>#</th>
//                   <th style={styles.th}>Name</th>
//                   <th style={styles.th}>Vip Id</th>
//                   <th style={styles.th}>Email</th>
//                   <th style={styles.th}>Designation</th>
//                   <th style={styles.th}>Contact</th>
//                   <th style={styles.th}>Status</th>
//                   {role !== "user" && <th style={styles.th}>Actions</th>}
//                 </tr>
//               </thead>

//               <tbody>
//                 {currentData.map((vip, index) => {
//                   const statusStyle =
//                     vip.status === "Active"
//                       ? styles.statusActive
//                       : styles.statusInactive;

//                   return (
//                     <tr
//                       key={vip.id || index}
//                       style={{
//                         ...styles.row,
//                         cursor: "pointer",
//                       }}
//                       onMouseEnter={(e) => {
//                         e.currentTarget.style.background = "#cfdcf6ff";
//                       }}
//                       onMouseLeave={(e) => {
//                         e.currentTarget.style.background = "transparent";
//                       }}
//                       onClick={() => {
//                         localStorage.setItem(
//                           "selectedVip",
//                           JSON.stringify(vip)
//                         );

//                         if (vip.status === "Inactive") {
//                           navigate("/dutyviphistory"); // ✅ GO TO DUTY HISTORY
//                         } else {
//                           navigate("/vipassignedofficers"); // ✅ EXISTING FLOW
//                         }
//                       }}
//                     >
//                       <td style={styles.td}>
//                         {index + 1 + (currentPage - 1) * rowsPerPage}
//                       </td>
//                       <td style={styles.td}>{vip.name}</td>
//                       <td style={styles.td}>{vip.id}</td>
//                       <td style={styles.td}>{vip.email}</td>
//                       <td style={styles.td}>
//                         <span style={styles.badge}>{vip.designation}</span>
//                       </td>
//                       <td style={styles.td}>{vip.contactno}</td>
//                       <td style={styles.td}>
//                         <span style={statusStyle}>{vip.status}</span>
//                       </td>

//                       {role !== "user" && (
//                         <td style={styles.actionCol}>
//                           <button
//                             style={styles.editBtn}
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               handleEdit(vip);
//                               navigate("/viplist"); // ✅ EXPLICIT EDIT
//                             }}
//                           >
//                             <i className="fa fa-edit"></i>
//                           </button>

//                           <button
//                             style={styles.deleteBtn}
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               handleDelete(vip);
//                             }}
//                           >
//                             <i className="fa fa-trash"></i>
//                           </button>
//                         </td>
//                       )}
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           )}

//           {/* SHIFT PAGINATION */}
//           {totalPages > 1 && (
//             <div style={styles.paginationContainer}>
//               <button
//                 disabled={currentPage === 1}
//                 style={styles.pageBtn}
//                 onClick={() => goToPage(currentPage - 1)}
//               >
//                 Prev
//               </button>

//               {visiblePages.map((page) => (
//                 <button
//                   key={page}
//                   style={{
//                     ...styles.pageBtn,
//                     ...(currentPage === page ? styles.activePage : {}),
//                   }}
//                   onClick={() => goToPage(page)}
//                 >
//                   {page}
//                 </button>
//               ))}

//               <button
//                 disabled={currentPage === totalPages}
//                 style={styles.pageBtn}
//                 onClick={() => goToPage(currentPage + 1)}
//               >
//                 Next
//               </button>

//               <select
//                 value={rowsPerPage}
//                 style={styles.rowsSelect}
//                 onChange={(e) => {
//                   setRowsPerPage(Number(e.target.value));
//                   setCurrentPage(1);
//                 }}
//               >
//                 <option value={30}>30 / Page</option>
//                 <option value={20}>20 / Page</option>
//                 <option value={10}>10 / Page</option>
//               </select>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

// /*  RESPONSIVE STYLES ONLY (DATA UNTOUCHED) */

// const styles = {
//   page: {
//     minHeight: "90vh",
//     width: "100%",
//     background:
//       "linear-gradient(135deg, rgba(145,195,243,0.7), rgba(233,239,125,0.9))",
//   },
//   headerRow: {
//     display: "flex",
//     flexWrap: "wrap",
//     justifyContent: "space-between",
//     padding: "12px 20px",
//     gap: 12,
//   },
//   title: { fontSize: 26, fontWeight: 800, color: "#1967d2" },
//   totalBox: {
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     background: "#e8f1ff",
//     padding: "10px 18px",
//     borderRadius: 12,
//   },

//   totalNumber: { fontSize: 22, fontWeight: 800, color: "#1967d2" },
//   totalLabel: { fontSize: 15, fontWeight: 600, color: "#1967d2" },
//   filterRow: {
//     display: "flex",
//     flexWrap: "wrap",
//     gap: 16,
//     padding: "0 20px 18px",
//   },
//   filterCard: {
//     flex: 3,
//     padding: 16,
//     borderRadius: 12,
//     background: "rgba(255,255,255,0.45)",
//     backdropFilter: "blur(8px)",
//     boxShadow: "0 4px 15px rgba(0,0,0,0.01)",
//   },
//   filterLabel: { fontSize: 14, fontWeight: 600 },
//   select: {
//     width: "100%",
//     marginTop: 6,
//     padding: 10,
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//   },
//   searchInput: {
//     width: "100%",
//     marginTop: 6,
//     padding: 10,
//     borderRadius: 8,
//     border: "1.5px solid #1967d2",
//   },
//   tableWrapper: { width: "100%", overflowX: "auto" },
//   tableCard: {
//     minWidth: 900,
//     margin: "0 20px",
//     background: "rgba(255,255,255,0.7)",
//     backdropFilter: "blur(8px)",
//     borderRadius: 12,
//     padding: 10,
//     boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
//   },
//   table: { width: "100%", borderCollapse: "collapse" },
//   th: {
//     background: "#b8b8f3ff",
//     padding: 12,
//     textAlign: "left",
//     fontWeight: 700,
//   },
//   td: {
//     padding: 10,
//     borderBottom: "2px solid rgba(25,103,210,0.15)", // ✅ stronger line
//   },

//   row: {
//     height: 48,
//     transition: "background 0.2s ease",
//   },

//   actionCol: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 7 },
//   editBtn: {
//     background: "#28a745",
//     padding: "6px 10px",
//     borderRadius: 6,
//     color: "#fff",
//     border: 0,
//     cursor: "pointer",
//   },
//   deleteBtn: {
//     background: "#fc4f30",
//     padding: "6px 10px",
//     borderRadius: 6,
//     color: "#fff",
//     border: 0,
//     cursor: "pointer",
//   },
//   statusActive: {
//     background: "#e6ffe6",
//     padding: "4px 10px",
//     borderRadius: 20,
//     color: "#2e7d32",
//     fontWeight: 600,
//   },
//   statusInactive: {
//     background: "#ffe6e6",
//     padding: "4px 10px",
//     borderRadius: 20,
//     color: "#d32f2f",
//     fontWeight: 600,
//   },
//   noData: { textAlign: "center", padding: 30, color: "#888" },
//   paginationContainer: {
//     display: "flex",
//     flexWrap: "wrap",
//     justifyContent: "center",
//     gap: 10,
//     padding: 15,
//   },
//   pageBtn: {
//     padding: "6px 14px",
//     borderRadius: 6,
//     border: "1px solid #1967d2",
//     background: "#fff",
//     cursor: "pointer",
//   },
//   activePage: { background: "#1967d2", color: "#fff", fontWeight: 700 },
//   rowsSelect: {
//     padding: "6px 14px",
//     borderRadius: 6,
//     border: "1px solid #1967d2",
//   },
// };
